import { useEffect, useState } from 'react';
import { Link } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LinkTransitionOverlayProps {
  show: boolean;
  onComplete: () => void;
}

export function LinkTransitionOverlay({ show, onComplete }: LinkTransitionOverlayProps) {
  const [phase, setPhase] = useState<'enter' | 'snap' | 'glow' | 'exit' | 'hidden'>('hidden');

  useEffect(() => {
    if (!show) {
      setPhase('hidden');
      return;
    }
    setPhase('enter');
    const t1 = setTimeout(() => setPhase('snap'), 250);
    const t2 = setTimeout(() => setPhase('glow'), 600);
    const t3 = setTimeout(() => setPhase('exit'), 1200);
    const t4 = setTimeout(() => {
      setPhase('hidden');
      onComplete();
      // Scroll to top after animation completes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [show, onComplete]);

  if (phase === 'hidden') return null;

  return (
    <div className={cn(
      'fixed inset-0 z-50 w-screen h-screen flex items-center justify-center bg-background/95 backdrop-blur-md transition-opacity duration-300',
      phase === 'exit' ? 'opacity-0' : 'opacity-100',
    )}>
      <div className={cn(
        'flex flex-col items-center gap-5 transition-all duration-400',
        phase === 'enter' && 'scale-50 opacity-0',
        phase === 'snap' && 'scale-110 opacity-100',
        phase === 'glow' && 'scale-100 opacity-100',
        phase === 'exit' && 'scale-100 opacity-100',
      )}>
        <div className={cn(
          'w-40 h-40 rounded-full flex items-center justify-center transition-all duration-400',
          phase === 'enter' && 'bg-muted',
          phase === 'snap' && 'bg-muted shadow-2xl',
          phase === 'glow' && 'bg-primary/20 shadow-[0_0_80px_rgba(2,132,199,0.6)]',
          phase === 'exit' && 'bg-primary/20',
        )}>
          <Link className={cn(
            'transition-all duration-400',
            phase === 'enter' && 'w-16 h-16 text-muted-foreground',
            phase === 'snap' && 'w-28 h-28 text-muted-foreground',
            (phase === 'glow' || phase === 'exit') && 'w-28 h-28 text-primary',
            phase === 'glow' && 'animate-[pulse_0.5s_ease-in-out_2]',
          )} />
        </div>
        <p className={cn(
          'text-lg font-bold tracking-wide transition-all duration-300',
          phase === 'glow' ? 'text-primary opacity-100' : 'opacity-0',
        )}>
          Compliance Chain Locked
        </p>
      </div>
    </div>
  );
}
